import { authErrorMessage, t } from '@glory/i18n';
import { AuthLayout, Button, Callout, EmailField } from '@glory/ui';
import { isEmail, normalizeEmail } from '@glory/ui/fields/parsers';
import { createFileRoute, Link } from '@tanstack/react-router';
import { type SubmitEvent, useState } from 'react';

import { authClient } from '../lib/auth-client.ts';

interface ForgotSearch {
  readonly email?: string | undefined;
}

export const Route = createFileRoute('/forgot-password')({
  validateSearch: (search: Record<string, unknown>): ForgotSearch => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const search = Route.useSearch();
  const [email, setEmail] = useState(search.email ?? '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const send = async (address: string) => {
    setPending(true);
    setError(null);
    // Ответ одинаковый, есть такой адрес или нет: по нему нельзя перебрать адреса сотрудников.
    const { error: failure } = await authClient.requestPasswordReset({ email: address });
    setPending(false);
    if (failure) {
      setError(authErrorMessage(failure));
      return;
    }
    setSentTo(address);
  };

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const address = normalizeEmail(email);
    if (!isEmail(address)) {
      setEmailError(t('fields.emailInvalid'));
      return;
    }
    void send(address);
  };

  if (sentTo) {
    return (
      <AuthLayout title={t('auth.resetSentTitle')} lead={t('auth.resetSentLead', { email: sentTo })}>
        <p className="text-small text-muted">{t('auth.resetSentHint')}</p>
        {error ? <Callout>{error}</Callout> : null}
        <div className="flex flex-col gap-2">
          <Button asChild variant="primary" size="lg">
            <Link to="/login">{t('auth.toLogin')}</Link>
          </Button>
          <Button size="lg" loading={pending} onClick={() => void send(sentTo)}>
            {t('auth.sendAgain')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.forgotTitle')} lead={t('auth.forgotLead')}>
      <form className="flex flex-col gap-4" noValidate onSubmit={submit}>
        <EmailField
          label={t('auth.email')}
          size="lg"
          autoFocus
          autoComplete="username"
          value={email}
          onValueChange={(value) => {
            setEmail(value);
            setEmailError(null);
          }}
          error={emailError}
        />
        {error ? <Callout>{error}</Callout> : null}
        <Button type="submit" variant="primary" size="lg" loading={pending}>
          {t('auth.sendResetLink')}
        </Button>
      </form>
      <Button asChild variant="ghost" size="sm" className="self-start">
        <Link to="/login">{t('auth.back')}</Link>
      </Button>
    </AuthLayout>
  );
}
